import React, { useEffect, useMemo, useState } from 'react';
import { customerProfileApi } from '../services/api';

type InboxCategory = 'all' | 'social' | 'friends';

type InboxItem = {
  id: string;
  category: string;
  title: string;
  preview?: string;
  timestamp?: string;
  unread?: boolean;
  business?: { id?: string; name?: string } | null;
};

const categories: Array<{ id: InboxCategory; label: string; icon: string }> = [
  { id: 'all', label: 'All Social', icon: '📥' },
  { id: 'social', label: 'Social Feed', icon: '🧑‍🤝‍🧑' },
  { id: 'friends', label: 'Friend Activity', icon: '💬' },
];

const SocialHub: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<InboxCategory>('all');
  const [items, setItems] = useState<InboxItem[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInbox = async (isRefresh = false, category: InboxCategory = activeCategory) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const response = await customerProfileApi.getInbox('all', 120);
      const rawItems = Array.isArray(response?.items) ? response.items : [];
      const socialItems = rawItems.filter((item) => item.category === 'social' || item.category === 'friends');
      const selected = category === 'all'
        ? socialItems
        : socialItems.filter((item) => item.category === category);
      setItems(selected);

      const unread = response?.unreadCounts || {};
      setUnreadCounts({
        social: unread.social || 0,
        friends: unread.friends || 0,
        all: (unread.social || 0) + (unread.friends || 0),
      });
    } catch (err) {
      console.error('Failed to load social inbox:', err);
      setError('Failed to load social streams.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadInbox(false, activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const groupedHint = useMemo(() => {
    if (activeCategory !== 'all') return null;
    return 'Social Hub keeps friend and social feed activity separate from business communication.';
  }, [activeCategory]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🧑‍🤝‍🧑 Social Hub</h1>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            All social and friend interactions in one place.
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadInbox(true)}
          disabled={refreshing}
          style={{
            padding: '10px 16px',
            border: '1px solid #ffb387',
            borderRadius: '10px',
            background: '#fff7f2',
            color: '#c2410c',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '14px', padding: '12px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '10px', fontSize: '16px', color: '#333' }}>Streams</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {categories.map((category) => {
              const isActive = category.id === activeCategory;
              const unread = unreadCounts[category.id] || 0;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategory(category.id)}
                  style={{
                    textAlign: 'left',
                    border: isActive ? '1px solid #ff9a56' : '1px solid #e5e7eb',
                    background: isActive ? '#fff7f2' : 'white',
                    borderRadius: '10px',
                    padding: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}
                >
                  <span style={{ color: '#333', fontWeight: isActive ? 700 : 500 }}>
                    <span style={{ marginRight: '8px' }}>{category.icon}</span>
                    {category.label}
                  </span>
                  {unread > 0 && (
                    <span style={{ background: '#dc2626', color: 'white', borderRadius: '999px', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                      {unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>Loading social inbox…</div>
          ) : (
            <>
              <h2 style={{ marginTop: 0, marginBottom: '6px', color: '#333' }}>
                {categories.find((category) => category.id === activeCategory)?.icon} {categories.find((category) => category.id === activeCategory)?.label}
              </h2>
              {groupedHint && <p style={{ marginTop: 0, color: '#666', fontSize: '13px' }}>{groupedHint}</p>}

              {items.length === 0 ? (
                <div style={{ padding: '18px', border: '1px dashed #e5e7eb', borderRadius: '10px', color: '#666' }}>
                  No items in this stream yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {items.map((item) => (
                    <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', background: item.unread ? '#fffbeb' : 'white' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ fontWeight: 700, color: '#333' }}>{item.title}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          {item.timestamp ? new Date(item.timestamp).toLocaleString() : '—'}
                        </div>
                      </div>
                      <div style={{ marginTop: '6px', color: '#555', fontSize: '14px' }}>{item.preview || 'No preview available'}</div>
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#777' }}>
                        {item.business?.name ? `Source: ${item.business.name}` : 'Source: social'} • {item.category}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialHub;
